import { useFileOrganizer } from "@via/hooks/useFileOrganizer.tsx";
import { type IImportedFile } from "@via/pages/data/file-organizer/file-organizer.tsx";
import {
  getFileNameWithoutPath,
  getMergedDirectoryPath,
  getNameWithPath,
  getSampleName,
  groupTripletFilesByPattern,
  ITripletItem,
} from "@via/pages/data/file-organizer/file-patterns/helpers.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { type JSX, useEffect, useState } from "react";
import { AutoMergePatternInput } from "@via/pages/data/file-organizer/file-patterns/auto-merge-pattern-input.tsx";
import { Sample } from "@via/context/file-organizer-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { DataSourceProvider } from "common";

export default function TripleList({
  fileList,
  globalBucketName,
  toBeRemovedFileIds,
  provider,
}: {
  fileList: IImportedFile[];
  globalBucketName: string;
  toBeRemovedFileIds: string[];
  provider: DataSourceProvider;
}): JSX.Element {
  const { setNotification, NOTIFICATION } = useNotifications();
  const { addNewSamples, resetSampleList, resetTrigger } = useFileOrganizer();

  const [inputR1, setInputR1] = useState<string>("_R1");
  const [inputR2, setInputR2] = useState<string>("_R2");
  const [inputR3, setInputR3] = useState<string>("_R3");

  const [listR1, setListR1] = useState<IImportedFile[]>([]);
  const [listR2, setListR2] = useState<IImportedFile[]>([]);
  const [listR3, setListR3] = useState<IImportedFile[]>([]);

  const [tripletList, setTripletList] = useState<ITripletItem[]>([]);

  /**
   * Effect to update items if their constituent file IDs are in toBeRemovedFileIds
   */
  useEffect(() => {
    if (toBeRemovedFileIds && toBeRemovedFileIds.length > 0) {
      setTripletList(prevTripletList =>
        prevTripletList.map(tripletItem => {
          const r1Id = tripletItem.fileR1?.id || "";
          const r2Id = tripletItem.fileR2?.id || "";
          const r3Id = tripletItem.fileR3?.id || "";
          if (
            (r1Id && toBeRemovedFileIds.includes(r1Id)) ||
            (r2Id && toBeRemovedFileIds.includes(r2Id)) ||
            (r3Id && toBeRemovedFileIds.includes(r3Id))
          ) {
            return {
              ...tripletItem,
              isChecked: false,
              isSampled: false,
            };
          }
          return tripletItem;
        })
      );
    }
  }, [toBeRemovedFileIds]);

  /**
   * Effects to filter fileList based on individual input patterns
   */
  useEffect(() => {
    setListR1(
      inputR1.trim() ? fileList.filter(item => item.name.includes(inputR1)) : []
    );
  }, [fileList, inputR1]);

  useEffect(() => {
    setListR2(
      inputR2.trim() ? fileList.filter(item => item.name.includes(inputR2)) : []
    );
  }, [fileList, inputR2]);

  useEffect(() => {
    setListR3(
      inputR3.trim() ? fileList.filter(item => item.name.includes(inputR3)) : []
    );
  }, [fileList, inputR3]);

  /**
   * Effect for the core triplet matching logic
   */
  useEffect(() => {
    const newTripletList: ITripletItem[] = [];
    const consumedR1Names: string[] = [];
    const consumedR2Names: string[] = [];
    const consumedR3Names: string[] = [];

    const r1Pattern = inputR1.trim();
    const r2Pattern = inputR2.trim();
    const r3Pattern = inputR3.trim();

    // Phase 1: Match R1 files with R2 and R3
    if (r1Pattern) {
      listR1.forEach(r1File => {
        if (consumedR1Names.includes(r1File.name)) return;

        const r1Prefix = r1File.name.split(r1Pattern)[0];

        const matchingR2 = r2Pattern
          ? listR2.find(
              r2File =>
                !consumedR2Names.includes(r2File.name) &&
                r2File.name.split(r2Pattern)[0] === r1Prefix &&
                r1File.name !== r2File.name
            )
          : undefined;

        const matchingR3 =
          matchingR2 && r3Pattern
            ? listR3.find(
                r3File =>
                  !consumedR3Names.includes(r3File.name) &&
                  r3File.name.split(r3Pattern)[0] === r1Prefix &&
                  r1File.name !== r3File.name &&
                  matchingR2.name !== r3File.name
              )
            : undefined;

        if (matchingR2 && matchingR3) {
          newTripletList.push({
            fileR1: r1File,
            fileR2: matchingR2,
            fileR3: matchingR3,
            isChecked: false,
            isSampled: false,
          });
          consumedR1Names.push(r1File.name);
          consumedR2Names.push(matchingR2.name);
          consumedR3Names.push(matchingR3.name);
        } else if (matchingR2) {
          // R1 + R2 found
          newTripletList.push({
            fileR1: r1File,
            fileR2: matchingR2,
            fileR3: undefined,
            isChecked: false,
            isSampled: false,
          });
          consumedR1Names.push(r1File.name);
          consumedR2Names.push(matchingR2.name);
        } else {
          // Only R1
          newTripletList.push({
            fileR1: r1File,
            fileR2: undefined,
            fileR3: undefined,
            isChecked: false,
            isSampled: false,
          });
          consumedR1Names.push(r1File.name);
        }
      });
    }

    // Phase 2: Match remaining R2 files with R3
    if (r2Pattern) {
      listR2.forEach(r2File => {
        if (consumedR2Names.includes(r2File.name)) return;
        const r2Prefix = r2File.name.split(r2Pattern)[0];

        const matchingR3 = r3Pattern
          ? listR3.find(
              r3File =>
                !consumedR3Names.includes(r3File.name) &&
                r3File.name.split(r3Pattern)[0] === r2Prefix &&
                r2File.name !== r3File.name
            )
          : undefined;

        if (matchingR3) {
          newTripletList.push({
            fileR1: undefined,
            fileR2: r2File,
            fileR3: matchingR3,
            isChecked: false,
            isSampled: false,
          });
          consumedR2Names.push(r2File.name);
          consumedR3Names.push(matchingR3.name);
        } else {
          // Only R2
          newTripletList.push({
            fileR1: undefined,
            fileR2: r2File,
            fileR3: undefined,
            isChecked: false,
            isSampled: false,
          });
          consumedR2Names.push(r2File.name);
        }
      });
    }

    // Phase 3: Add remaining R3 files
    if (r3Pattern) {
      listR3.forEach(r3File => {
        if (consumedR3Names.includes(r3File.name)) return;
        newTripletList.push({
          fileR1: undefined,
          fileR2: undefined,
          fileR3: r3File,
          isChecked: false,
          isSampled: false,
        });
      });
    }

    if (!r1Pattern && !r2Pattern && !r3Pattern && fileList.length > 0) {
      fileList.forEach(file => {
        newTripletList.push({
          fileR1: file,
          fileR2: undefined,
          fileR3: undefined,
          isChecked: false,
          isSampled: false,
        });
      });
    }

    setTripletList(newTripletList);
  }, [listR1, listR2, listR3, inputR1, inputR2, inputR3, fileList]);

  const handleInputChange = (
    value: string,
    targetInput: "R1" | "R2" | "R3"
  ): void => {
    if (targetInput === "R1") setInputR1(value);
    else if (targetInput === "R2") setInputR2(value);
    else setInputR3(value);
  };

  const handleTripletListClick = (clickedItem: ITripletItem): void => {
    setTripletList(prevList =>
      prevList.map(item =>
        item === clickedItem ? { ...item, isChecked: !item.isChecked } : item
      )
    );
  };

  const getIds = (triplet: ITripletItem): string => {
    return [triplet.fileR1?.id, triplet.fileR2?.id, triplet.fileR3?.id]
      .filter(Boolean)
      .join(",");
  };

  const getTripleIdsReordered = (triplets: ITripletItem[]): string => {
    const r1Ids: string[] = [];
    const r2Ids: string[] = [];
    const r3Ids: string[] = [];

    triplets.forEach(triplet => {
      if (triplet.fileR1?.id) r1Ids.push(triplet.fileR1.id);
      if (triplet.fileR2?.id) r2Ids.push(triplet.fileR2.id);
      if (triplet.fileR3?.id) r3Ids.push(triplet.fileR3.id);
    });

    // Combine all R1 first, then R2, then R3
    return [...r1Ids, ...r2Ids, ...r3Ids].join(",");
  };

  const getFilesUsedForSample = (triplet: ITripletItem): string => {
    return [
      getFileNameWithoutPath(triplet.fileR1?.name || ""),
      getFileNameWithoutPath(triplet.fileR2?.name || ""),
      getFileNameWithoutPath(triplet.fileR3?.name || ""),
    ]
      .filter(Boolean)
      .join(",");
  };

  const getDirectoriesForSample = (triplet: ITripletItem): string => {
    return [
      triplet.fileR1?.name || "",
      triplet.fileR2?.name || "",
      triplet.fileR3?.name || "",
    ]
      .filter(Boolean)
      .join(",");
  };

  const updateItemsToSampled = (itemsToUpdate: ITripletItem[]) => {
    setTripletList(prevList =>
      prevList.map(currentItem => {
        if (itemsToUpdate.some(updatedItem => updatedItem === currentItem)) {
          return { ...currentItem, isSampled: true, isChecked: false };
        }
        return currentItem;
      })
    );
  };

  const handleAutoMergeClick = (autoMergePattern: string): void => {
    const unSampledTriplets = tripletList.filter(
      t => !t.isSampled && t.fileR1 && t.fileR2 && t.fileR3
    );

    try {
      const grouped = groupTripletFilesByPattern(
        unSampledTriplets,
        autoMergePattern
      );

      const toBeAddedSamples: Sample[] = [];

      Object.entries(grouped).forEach(([groupKey, items]) => {
        const mergedSample = {
          id: "",
          name: groupKey,
          filesUsed: "",
          directory: "",
          file1: "",
          file2: "",
          file3: "",
          "Provider IDs": "",
        };

        items.forEach((item, index) => {
          item.isSampled = true;

          const ids = getIds(item);
          const files = getFilesUsedForSample(item);
          const dirs = getDirectoriesForSample(item);

          if (index > 0) {
            mergedSample.id += ",";
            mergedSample.filesUsed += " | ";
            mergedSample.directory += ",";
            mergedSample.file1 += ",";
            mergedSample.file2 += ",";
            mergedSample.file3 += ",";
            mergedSample["Provider IDs"] += ",";
          }

          mergedSample.id += ids;
          mergedSample.filesUsed += files;
          mergedSample.directory += dirs;
          mergedSample.file1 += getNameWithPath(
            item.fileR1?.path || "",
            item.fileR1?.displayName || "",
            item.fileR1?.name || "",
            provider
          );
          mergedSample.file2 += getNameWithPath(
            item.fileR2?.path || "",
            item.fileR2?.displayName || "",
            item.fileR2?.name || "",
            provider
          );
          mergedSample.file3 += getNameWithPath(
            item.fileR3?.path || "",
            item.fileR3?.displayName || "",
            item.fileR3?.name || "",
            provider
          );
        });

        if (provider === DataSourceProvider.BaseSpace) {
          mergedSample["Provider IDs"] = getTripleIdsReordered(items);
        }

        mergedSample.directory = getMergedDirectoryPath(
          mergedSample.directory,
          globalBucketName
        );

        toBeAddedSamples.push(mergedSample);
      });

      addNewSamples(toBeAddedSamples);
    } catch (error) {
      setNotification({
        id: "triple-auto-merge-failed",
        message: "Auto Merge Failed",
        details: "Invalid Pattern",
        variant: NOTIFICATION.ERROR,
      });
    }
  };

  const handleResetClick = (): void => {
    resetSampleList();
    setTripletList(prevList =>
      prevList.map(item => ({ ...item, isChecked: false, isSampled: false }))
    );
  };

  // Watch for reset trigger from context (e.g., when vmetaProjectId changes)
  useEffect(() => {
    if (resetTrigger > 0) {
      handleResetClick();
    }
  }, [resetTrigger]);

  const handleMergeSelectedFilesClick = (): void => {
    const selectedTriplets = tripletList.filter(
      item => item.isChecked && !item.isSampled
    );

    if (selectedTriplets.length > 0) {
      const mergedSample = {
        id: "",
        name: "",
        filesUsed: "",
        directory: "",
        file1: "",
        file2: "",
        file3: "",
        "Provider IDs": "",
      };
      const firstFileForName =
        selectedTriplets[0].fileR1 ||
        selectedTriplets[0].fileR2 ||
        selectedTriplets[0].fileR3;

      mergedSample.name = getSampleName(
        getFileNameWithoutPath(firstFileForName?.name || "merged_triplet"),
        inputR1.trim() || inputR2.trim() || inputR3.trim() || "."
      );

      selectedTriplets.forEach((triplet, index) => {
        if (index > 0) {
          mergedSample.id += ",";
          mergedSample.filesUsed += " | ";
          mergedSample.directory += ",";
          mergedSample.file1 += ",";
          mergedSample.file2 += ",";
          mergedSample.file3 += ",";
          mergedSample["Provider IDs"] += ",";
        }
        mergedSample.id += getIds(triplet);
        mergedSample.filesUsed += getFilesUsedForSample(triplet);
        mergedSample.directory += getDirectoriesForSample(triplet);
        mergedSample.file1 += getNameWithPath(
          triplet.fileR1?.path || "",
          triplet.fileR1?.displayName || "",
          triplet.fileR1?.name || "",
          provider
        );
        mergedSample.file2 += getNameWithPath(
          triplet.fileR2?.path || "",
          triplet.fileR2?.displayName || "",
          triplet.fileR2?.name || "",
          provider
        );
        mergedSample.file3 += getNameWithPath(
          triplet.fileR3?.path || "",
          triplet.fileR3?.displayName || "",
          triplet.fileR3?.name || "",
          provider
        );
      });

      if (provider === DataSourceProvider.BaseSpace) {
        mergedSample["Provider IDs"] = getTripleIdsReordered(selectedTriplets);
      }

      mergedSample.directory = getMergedDirectoryPath(
        mergedSample.directory,
        globalBucketName
      );
      addNewSamples([mergedSample]);
      updateItemsToSampled(selectedTriplets);
    }
  };

  const handleAddSelectedFilesClick = (): void => {
    const selectedItems = tripletList.filter(
      item => item.isChecked && !item.isSampled
    );
    if (selectedItems.length === 0) return;

    const samplesToAdd = selectedItems.map(triplet => {
      const firstFileForName =
        triplet.fileR1 || triplet.fileR2 || triplet.fileR3;
      return {
        id: getIds(triplet),
        name: getSampleName(
          getFileNameWithoutPath(firstFileForName?.name || "triplet_sample"),
          inputR1.trim() || inputR2.trim() || inputR3.trim() || "."
        ),
        filesUsed: getFilesUsedForSample(triplet),
        directory: getMergedDirectoryPath(
          getDirectoriesForSample(triplet),
          globalBucketName
        ),
        file1: getNameWithPath(
          triplet.fileR1?.path || "",
          triplet.fileR1?.displayName || "",
          triplet.fileR1?.name || "",
          provider
        ),
        file2: getNameWithPath(
          triplet.fileR2?.path || "",
          triplet.fileR2?.displayName || "",
          triplet.fileR2?.name || "",
          provider
        ),
        file3: getNameWithPath(
          triplet.fileR3?.path || "",
          triplet.fileR3?.displayName || "",
          triplet.fileR3?.name || "",
          provider
        ),
        "Provider IDs":
          provider === DataSourceProvider.BaseSpace ? getIds(triplet) : "",
      };
    });

    addNewSamples(samplesToAdd);
    updateItemsToSampled(selectedItems);
  };

  const handleAddAllFilesClick = (): void => {
    const allNonSampledTriplets = tripletList.filter(
      item =>
        !item.isSampled &&
        item.fileR1 !== undefined &&
        item.fileR2 !== undefined &&
        item.fileR3 !== undefined
    );
    if (allNonSampledTriplets.length === 0) return;

    const samplesToAdd = allNonSampledTriplets.map(triplet => {
      const firstFileForName =
        triplet.fileR1 || triplet.fileR2 || triplet.fileR3;
      return {
        id: getIds(triplet),
        name: getSampleName(
          getFileNameWithoutPath(firstFileForName?.name || "triplet_sample"),
          inputR1.trim() || inputR2.trim() || inputR3.trim() || "."
        ),
        filesUsed: getFilesUsedForSample(triplet),
        directory: getMergedDirectoryPath(
          getDirectoriesForSample(triplet),
          globalBucketName
        ),
        file1: getNameWithPath(
          triplet.fileR1?.path || "",
          triplet.fileR1?.displayName || "",
          triplet.fileR1?.name || "",
          provider
        ),
        file2: getNameWithPath(
          triplet.fileR2?.path || "",
          triplet.fileR2?.displayName || "",
          triplet.fileR2?.name || "",
          provider
        ),
        file3: getNameWithPath(
          triplet.fileR3?.path || "",
          triplet.fileR3?.displayName || "",
          triplet.fileR3?.name || "",
          provider
        ),
        "Provider IDs":
          provider === DataSourceProvider.BaseSpace ? getIds(triplet) : "",
      };
    });
    addNewSamples(samplesToAdd);
    updateItemsToSampled(allNonSampledTriplets);
  };

  const buttonDetails = [
    {
      label: "Reset",
      onClick: handleResetClick,
      testId: "tripletListResetButton",
    },
    {
      label: "Merge Selected Files",
      onClick: handleMergeSelectedFilesClick,
      testId: "tripletListMergeButton",
    },
    {
      label: "Add Selected Files",
      onClick: handleAddSelectedFilesClick,
      testId: "tripletListAddSelectedButton",
    },
    {
      label: "Add All Files",
      onClick: handleAddAllFilesClick,
      testId: "tripletListAddAllButton",
    },
  ];

  return (
    <div className="mt-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
        {[
          {
            label: "R1 Pattern",
            value: inputR1,
            setter: (val: string) => handleInputChange(val, "R1"),
            testId: "r1PatternInputTriple",
          },
          {
            label: "R2 Pattern",
            value: inputR2,
            setter: (val: string) => handleInputChange(val, "R2"),
            testId: "r2PatternInputTriple",
          },
          {
            label: "R3 Pattern",
            value: inputR3,
            setter: (val: string) => handleInputChange(val, "R3"),
            testId: "r3PatternInputTriple",
          },
        ].map(inputProps => (
          <div
            key={inputProps.label}
            className="flex flex-1 items-center gap-2">
            <label
              htmlFor={inputProps.testId}
              className="block whitespace-nowrap text-sm font-medium leading-6 text-gray-900">
              {inputProps.label}
            </label>
            <input
              id={inputProps.testId}
              data-testid={inputProps.testId}
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              type="text"
              value={inputProps.value}
              onChange={e => inputProps.setter(e.target.value)}
            />
          </div>
        ))}
      </div>

      <div
        data-testid="tripletListDiv"
        className="mt-4 flex h-60 flex-col overflow-auto rounded-md border border-gray-200">
        {tripletList.length === 0 ? (
          <div />
        ) : (
          tripletList.map((item, index) =>
            item.isSampled ? null : (
              <div
                className={classNames(
                  "flex cursor-pointer gap-3 px-2 py-2 hover:bg-gray-50 text-sm",
                  item.isChecked ? "bg-indigo-100 hover:bg-indigo-200" : ""
                )}
                key={`${getIds(item)}-${index}`}
                onClick={() => handleTripletListClick(item)}>
                {[item.fileR1, item.fileR2, item.fileR3].map(
                  (file, fileIndex) => (
                    <div
                      key={fileIndex}
                      className="flex-1"
                      title={getFileNameWithoutPath(file?.name || "") || ""}>
                      {getFileNameWithoutPath(file?.name || "") || <span />}
                    </div>
                  )
                )}
              </div>
            )
          )
        )}
      </div>

      <AutoMergePatternInput onAutoMergeClick={handleAutoMergeClick} />

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        {buttonDetails.map(button => {
          return (
            <button
              data-testid={button.testId}
              key={button.label}
              onClick={button.onClick}
              className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              {button.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
